import {
    EventName,
    EventTimekeeping,
    HalfEvent,
    MatchEvent,
    StoppageEvent,
    SuspensionEvent,
} from "refsix-js-models";
import {
    suspensionMillisBefore,
    suspensionMillisInPeriod,
    suspensionsIn,
} from "./SuspensionHelper";

// REFSIX-745: under a stopping clock an event is shown at the minute the clock
// would have reached had it stopped for every stoppage recorded before it in
// the same period. Recorded minutes are never changed; this is display only.

export function isStoppingClock(match: {
    eventTimekeeping?: EventTimekeeping;
}): boolean {
    return match.eventTimekeeping === EventTimekeeping.stoppingClock;
}

function stoppagesIn(events: MatchEvent[], half: number): StoppageEvent[] {
    return events.filter(
        (event) => event.eventName === EventName.stoppage && event.half === half
    ) as StoppageEvent[];
}

// Milliseconds of stoppage in the event's period up to the moment it happened.
// A stoppage still running at that moment counts only up to the event.
export function stoppageMillisBefore(
    event: MatchEvent,
    events: MatchEvent[]
): number {
    return stoppagesIn(events, event.half)
        .filter((stoppage) => stoppage.timestamp < event.timestamp)
        .reduce(
            (total, stoppage) =>
                total +
                Math.min(stoppage.endTime ?? event.timestamp, event.timestamp) -
                stoppage.timestamp,
            0
        );
}

// Milliseconds of stoppage across a whole period, for the period-end row.
export function stoppageMillisInPeriod(
    period: HalfEvent,
    events: MatchEvent[]
): number {
    const periodEnd = period.endTime ?? Date.now();
    return stoppagesIn(events, period.index).reduce(
        (total, stoppage) =>
            total +
            Math.min(stoppage.endTime ?? periodEnd, periodEnd) -
            stoppage.timestamp,
        0
    );
}

// The 1-based minute within its period an event is shown at under a stopping
// clock. Same rounding as the watches' minuteInHalf: floor(elapsed) + 1.
// Suspended time (REFSIX-647) is never match time, so it comes off as well.
export function stoppingClockMinuteInPeriod(
    event: MatchEvent,
    periodStart: number,
    events: MatchEvent[]
): number {
    const played =
        event.timestamp -
        periodStart -
        suspensionMillisBefore(event.timestamp, event.half, events) -
        stoppageMillisBefore(event, events);
    return Math.floor(played / 60000) + 1;
}

// Match time the period ran for, for the period-end row. Suspended time
// (REFSIX-647) is never match time; stoppages come off under a stopping clock.
export function playedMillisInPeriod(
    period: HalfEvent,
    events: MatchEvent[],
    stoppingClock: boolean
): number {
    const periodEnd = period.endTime ?? Date.now();
    return (
        periodEnd -
        period.timestamp -
        suspensionMillisInPeriod(period, events) -
        (stoppingClock ? stoppageMillisInPeriod(period, events) : 0)
    );
}

// The inverse of stoppingClockMinuteInPeriod: the moment the stopping clock
// reaches the start of a 1-based minute of the period. Places an event the
// referee picks by the minute they saw. The clock stands still through every
// stoppage and, as suspended time is never match time (REFSIX-647), every
// suspension; the two never overlap, as suspending ends a running stoppage.
export function stoppingClockMinuteStart(
    minuteInPeriod: number,
    period: HalfEvent,
    events: MatchEvent[]
): number {
    const periodEnd = period.endTime ?? Date.now();
    const clockStopped: Array<StoppageEvent | SuspensionEvent> = [
        ...stoppagesIn(events, period.index),
        ...suspensionsIn(events, period.index),
    ];
    return clockStopped
        .sort((a, b) => a.timestamp - b.timestamp)
        .reduce(
            (timestamp, stopped) =>
                stopped.timestamp < timestamp
                    ? timestamp +
                      Math.min(stopped.endTime ?? periodEnd, periodEnd) -
                      stopped.timestamp
                    : timestamp,
            period.timestamp + (minuteInPeriod - 1) * 60000
        );
}

// Finds the period an event was recorded in.
export function periodOf(
    event: MatchEvent,
    events: MatchEvent[]
): HalfEvent | undefined {
    return events.find(
        (candidate) =>
            candidate.eventName === EventName.half &&
            (candidate as HalfEvent).index === event.half
    ) as HalfEvent | undefined;
}
